import { type FC, type PropsWithChildren, useEffect, useRef, useState } from "react";
import {
  applyDocumentAuthenticationState,
  getTokenFromLocalStorage,
  AuthContext,
  LOCALSTORAGE_TOKEN_KEY,
} from "./auth";
import { useQueryClient } from "@tanstack/react-query";

export const AuthProvider: FC<PropsWithChildren> = ({ children }) => {
  const [token, setTokenState] = useState<string | null>(getTokenFromLocalStorage);
  const queryClient = useQueryClient();
  const previousTokenRef = useRef(token);

  const setToken: React.Dispatch<React.SetStateAction<string | null>> = (value) => {
    setTokenState((currentToken) => {
      const nextToken = typeof value === "function" ? value(currentToken) : value;

      if (nextToken) {
        localStorage.setItem(LOCALSTORAGE_TOKEN_KEY, nextToken);
      } else {
        localStorage.removeItem(LOCALSTORAGE_TOKEN_KEY);
        queryClient.clear();
      }

      return nextToken;
    });
  };

  useEffect(() => {
    if (token) {
      localStorage.setItem(LOCALSTORAGE_TOKEN_KEY, token);
    }
  }, [token]);

  useEffect(() => {
    applyDocumentAuthenticationState(Boolean(token));
  }, [token]);

  useEffect(() => {
    const previousToken = previousTokenRef.current;

    if (previousToken && token && previousToken !== token) {
      queryClient.clear();
    }

    previousTokenRef.current = token;
  }, [token, queryClient]);

  const logout = () => {
    setToken(null);
  };

  const isAuthenticated = !!token;

  return <AuthContext.Provider value={{ isAuthenticated, token, setToken, logout }}>{children}</AuthContext.Provider>;
};
